import { useEffect, useState } from "react";
import { api, STATUS_COLORS } from "../api";
import { useAuth } from "../context/AuthContext";
import { useLang, fmtRM, fmtDate } from "../i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Flag } from "lucide-react";
import { toast } from "sonner";

export default function Claims() {
  const { lang } = useLang();
  const bm = lang === "bm";
  const [claims, setClaims] = useState([]);
  const [types, setTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "", amount: "", claim_date: "", description: "" });

  const load = () => {
    api.get("/claims", { params: { scope: "mine" } }).then((r) => setClaims(r.data)).catch(() => {});
    api.get("/claims/types").then((r) => setTypes(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      const r = await api.post("/claims", { ...form, amount: parseFloat(form.amount) });
      if (r.data.flags?.length) toast.warning(r.data.flags[0]);
      else toast.success(bm ? "Tuntutan dihantar" : "Claim submitted");
      setOpen(false);
      setForm({ type: "", amount: "", claim_date: "", description: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const selectedPolicy = types.find((t) => t.type === form.type)?.policy;

  return (
    <div data-testid="claims-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{bm ? "Tuntutan" : "Claims"}</h1>
        <Button data-testid="claim-submit-open-button" className="bg-blue-800 hover:bg-blue-900" onClick={() => setOpen(true)}>
          <Plus className="w-4 h-4 mr-1" /> {bm ? "Hantar Tuntutan" : "New Claim"}
        </Button>
      </div>

      <div className="bg-card border rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{bm ? "Jenis" : "Type"}</TableHead>
              <TableHead>{bm ? "Tarikh" : "Date"}</TableHead>
              <TableHead className="hidden md:table-cell">{bm ? "Keterangan" : "Description"}</TableHead>
              <TableHead className="text-right">{bm ? "Jumlah" : "Amount"}</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="hidden lg:table-cell">Flags</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {claims.map((c) => (
              <TableRow key={c.claim_id} data-testid={`claim-row-${c.claim_id}`}>
                <TableCell className="text-sm font-medium">{c.type}</TableCell>
                <TableCell className="text-sm">{fmtDate(c.claim_date)}</TableCell>
                <TableCell className="hidden md:table-cell text-sm text-slate-600">{c.description}</TableCell>
                <TableCell className="text-right font-mono text-sm">{fmtRM(c.amount)}</TableCell>
                <TableCell><Badge variant="outline" className={STATUS_COLORS[c.status] || ""}>{c.status}</Badge></TableCell>
                <TableCell className="hidden lg:table-cell">
                  {c.flags?.length > 0 && (
                    <span className="text-xs text-amber-700 flex items-center gap-1"><Flag className="w-3 h-3" /> REVIEW REQUIRED</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {claims.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-500 py-8">{bm ? "Tiada tuntutan lagi." : "No claims yet."}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="claim-submit-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>{bm ? "Tuntutan Baharu" : "New Claim"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">{bm ? "Jenis Tuntutan" : "Claim Type"}</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="claim-type-select"><SelectValue placeholder="-" /></SelectTrigger>
                <SelectContent>{types.map((t) => <SelectItem key={t.type} value={t.type}>{t.type}</SelectItem>)}</SelectContent>
              </Select>
              {selectedPolicy?.limit && (
                <p className="text-xs text-slate-500">{bm ? `Had polisi: RM${selectedPolicy.limit}/${selectedPolicy.period}` : `Policy limit: RM${selectedPolicy.limit}/${selectedPolicy.period}`}</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">{bm ? "Jumlah (RM)" : "Amount (RM)"}</Label>
                <Input data-testid="claim-amount-input" type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{bm ? "Tarikh" : "Date"}</Label>
                <Input data-testid="claim-date-input" type="date" value={form.claim_date} onChange={(e) => setForm({ ...form, claim_date: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{bm ? "Keterangan" : "Description"}</Label>
              <Textarea data-testid="claim-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <Button data-testid="claim-submit-button" className="w-full bg-blue-800 hover:bg-blue-900" onClick={submit}>
              {bm ? "Hantar" : "Submit Claim"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
